import { ProfilePicture } from "@/bapi-client/types/users";
import { toAlphanumeric } from "@/utils/text";

import type { UserExtended } from "@/bapi-client/types/user";

// OpenAPI
import type { AdminUserSchema } from "@telegraphio/bapi-types";

export interface APICustomerUser {
  user_id: string;
  first_name: string;
  last_name: string;
  is_admin: boolean;
  status: number;
  zip_code: string | null;
  phone_number_work: string | null;
  email: string;
  external_idp?: string;
  user_token: boolean;
  companies: {
    company_id: string;
    company_name?: string;
    role_id: number;
    role_name: string;
    status: number;
    name?: string;
    id?: string;
  }[];
  profile_picture?: {
    normalized_url: string;
    thumbnail_url: string;
    raw_url: string;
    filename: string;
    mime_type: string;
  };
}

export interface CustomerUser {
  user_id: string;
  first_name: string;
  last_name: string;
  full_name: string;
  is_admin: boolean;
  status: number;
  zip_code: string | null;
  phone_number_work: string | null;
  external_idp?: string;
  email: string;
  user_token?: boolean;
  customers: {
    customer_id: string;
    customer_name: string;
    role_id: number;
    role_name: string;
  }[];
  search_tag: string;
  profile_picture?: ProfilePicture;
}

export function makeCustomerUser(data: APICustomerUser): CustomerUser {
  const user: CustomerUser = {
    ...data,
    get fullName() {
      return `${this.first_name} ${this.last_name}`;
    },
    customers: data.companies
      .filter((company) => {
        if (company.status === undefined) return true;
        else return company.status === 1;
      })
      .map((customer) => ({
        customer_id: customer.id ?? customer.company_id,
        customer_name: customer.company_name ?? customer.name ?? "",
        role_id: customer.role_id,
        role_name: customer.role_name,
      })),
    get searchTag() {
      return toAlphanumeric(
        `${this.first_name}${this.last_name}${this.email}${this.customers.map((cust) => cust.customer_name).join("")}`,
      );
    },
    full_name: "",
    search_tag: "",
  };
  if (data.profile_picture) {
    user.profile_picture = data.profile_picture;
  }
  return user;
}

// OpenAPI

export type InternalCustomerUser = AdminUserSchema & {
  full_name: string;
  search_tag: string;
};

export function makeAdminCustomerUser(data: AdminUserSchema): InternalCustomerUser {
  const { profile_picture, ...rest } = data;
  const user: InternalCustomerUser = {
    ...rest,
    get full_name() {
      return `${this.first_name} ${this.last_name}`;
    },
    get search_tag() {
      return toAlphanumeric(
        `${this.first_name}${this.last_name}${this.email}${this.customers?.map((cust) => cust.name).join("")}`,
      );
    },
    profile_picture: data.profile_picture,
  };
  return user;
}

// This is a temporary function to convert a UserExtended to an InternalCustomerUser
// TODO: Remove this function when we no longer need to support the old user schema
export function makeAdminCustomerUserFromUserExtended(data: UserExtended): InternalCustomerUser {
  const user: InternalCustomerUser = {
    user_id: data.user_id,
    first_name: data.first_name,
    last_name: data.last_name,
    email: data.email,
    is_admin: data.is_admin,
    is_internal: data.is_internal,
    status: data.status as 0 | 1,
    zip_code: data.zip_code,
    phone_number_work: data.phone_number_work,
    external_idp: data.external_idp,
    token: data.token,
    customers: data.companies.map((company) => ({
      id: company.id || "",
      name: company.name || "",
      logo_url: company.logo || "",
      role_id: company.role_id,
      role_name: company.role_name,
    })),
    profile_picture: data.profile_picture,
    full_name: `${data.first_name} ${data.last_name}`,
    search_tag: toAlphanumeric(
      `${data.first_name}${data.last_name}${data.email}${data.companies.map((company) => company.name || "").join("")}`,
    ),
  };
  return user;
}
